import {InfiniteData} from "@tanstack/query-core/src/types";
import {
    AddressItemType,
    BatchAddressType,
    GetItemCounts,
    PaginatedResponseType,
    useInfiniteAddresses
} from "@twigsby/client-data";
import {AddressType} from "@twigsby/client-data/lib/Types/AddressTypes";
import {
    AlertBanner, ColourSolid,
    ComboBoxControl,
    FlexGap,
    StackVertical
} from "@twigsby/frontend-framework";
import {GetArrayIndexByKeyValue} from "@twigsby/helpers";
import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "@twigsby/frontend-framework";
import {AddAddressTypeButtons} from "./AddAddressTypeButtons";
import {AddressListItemType} from "./SendsAdmin/CreateSendsForm";

export interface AddressControlType {
    extraAddressData?: AddressItemType;
    extraForwardingAddressData?: BatchAddressType;
    showCreateAddressOptions?: boolean;
    hideImportOptions?: boolean;

    name: string;
    label?: string;
    placeholder?: string;
    minimal?: boolean;
    defaultValues?: Array<any>;
    singleSelection?: boolean;
    forwardValueCallback?: (values: any) => void;
    forwardValuesCallback?: (values: Array<any>) => void;
    forwardItemCallback?: (value: AddressItemType, checked: boolean) => void;
    forwardFullAddressesCallback?: (values: Array<any>) => void;

    newAddressCallback?: (addressId: AddressType) => void;
    bulkAddressCallback?: (addresses: Array<AddressType>) => void;

    loading?: boolean;
    disabled?: boolean;

    reset?: boolean;
    required?: boolean;
    requiredLabel?: string;
}

const AddressControl = (props: AddressControlType) => {
    const { t } = useTranslation();

    const {
        status,
        data,
        error,
        dataUpdatedAt,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage
    } = useInfiniteAddresses(50);

    const [ addressOptions, setAddressOptions ] = useState<Array<any>>([]);
    const [ addressItemCounts, setAddressItemCounts] = useState<any>({ loaded: 0, total: 0});

    const MapAddressOptions = (data: InfiniteData<PaginatedResponseType>) => {
        setAddressItemCounts(GetItemCounts(data));

        const newAddresses = data?.pages.map((page) => page?.data && page.data.map((address) => {
            return { 'label': address.label, 'value': address.value };
        }))[0];

        let shapedExtraAddress: AddressItemType | undefined;

        if(props.extraAddressData || props.extraForwardingAddressData) {
            const existingIndex = GetArrayIndexByKeyValue(newAddresses, 'value', props.extraForwardingAddressData ? props.extraForwardingAddressData.addressId : props.extraAddressData?.object.addressId);

            if(existingIndex === -1) {
                shapedExtraAddress = props.extraForwardingAddressData ? {
                    value: props.extraForwardingAddressData.addressId,
                    label: props.extraForwardingAddressData.formatted,
                    object: {
                        addressId: props.extraForwardingAddressData.addressId,
                        name: props.extraForwardingAddressData.name,
                        businessName: props.extraForwardingAddressData.businessName,
                        propertyName: props.extraForwardingAddressData.propertyName,
                        addressLine1: props.extraForwardingAddressData.addressLine1,
                        addressLine2: props.extraForwardingAddressData.addressLine2,
                        addressLine3: props.extraForwardingAddressData.addressLine3,
                        countryCode: props.extraForwardingAddressData.countryCode,
                        emailAddress: '',
                        phoneNo: '',
                        postCode: props.extraForwardingAddressData.postCode,
                    }
                } : props.extraAddressData;
            }
        }

        const newData = shapedExtraAddress ? [ shapedExtraAddress, ...newAddresses.flat() ] : newAddresses.flat();

        setAddressOptions(newData);

        if(props.defaultValues) {
            HandleItemsChanged(props.defaultValues[0], true, newData);
        }
    }

    const HandleValueChanged = (e: any) => {
        if(typeof(e) === 'object' && props.forwardValuesCallback) {
            HandleForwardFullAddresses(e);
            props.forwardValuesCallback(e);
        }

        if(typeof(e) === 'number' && props.forwardValueCallback) {
            HandleForwardFullAddresses([e]);
            props.forwardValueCallback(e);
        }
    }

    const HandleForwardFullAddresses = (e: Array<number>) => {
        if(props.forwardFullAddressesCallback) {
            props.forwardFullAddressesCallback(BuildFullAddresses(e));
        }
    }

    const HandleItemsChanged = useCallback((e: number, checked: boolean, forwardedData?: Array<any>) => {
        if(props.forwardItemCallback) {
            let addressItem;

            if(forwardedData) {
                forwardedData.forEach((address) => {
                    if(address.value === e) {
                        addressItem = {
                            label: address.label,
                            value: address.value,
                            object: {
                                addressId: address.addressId || address.value,
                                addressString: address.addressString || address.label,
                                name: address.name || '',
                                businessName: address.businessName || '',
                                propertyName: address.propertyName || '',
                                addressLine1: address.addressLine1 || '',
                                addressLine2: address.addressLine2 || '',
                                addressLine3: address.addressLine3 || '',
                                postCode: address.postCode || '',
                                countryCode: address.countryCode || address.label.split(',').pop().trim(),
                                phoneNo: address.phoneNo || '',
                                emailAddress: address.emailAddress || '',
                            }
                        };
                    }
                })
            } else {
                data?.pages.forEach((page) => page?.data && page.data.forEach((address) => {
                    if(address.object.addressId === e) {
                        addressItem = address;
                    }
                }));
            }

            if(addressItem) {
                props.forwardItemCallback(addressItem, checked);
            }
        }
    }, [data, addressOptions]);

    const BuildFullAddresses = (e: Array<number>) => {
        let fullAddresses: Array<any> = [];

        data?.pages.map((page: any) =>
            page?.data && page.data.forEach((address: AddressListItemType) => {
                if(e.includes(address.value)) {
                    fullAddresses.push(address);
                }
            })
        )

        return fullAddresses;
    }

    useEffect(() => {
        if(data) {
            MapAddressOptions(data);
        }
    }, [data, dataUpdatedAt, props.extraForwardingAddressData]);

    if(status === 'error') {
        return <AlertBanner title={error?.message} colourMod={ColourSolid.Alert} />
    }

    return (
        <StackVertical gap={FlexGap.ExtraSmall}>
            <ComboBoxControl
                name={props.name}
                placeholder={props.placeholder || t('controls.addressPlaceholder')}
                data={addressOptions}
                defaultValues={props.defaultValues ? props.defaultValues : undefined}
                loading={status === 'loading' || !addressOptions || props.loading}
                valueType={'number'}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                itemCounts={addressItemCounts}
                singleSelection={props.singleSelection}
                forwardItemCallback={(e, checked) => HandleItemsChanged(e.value, checked) }
                forwardValueCallback={(e) => HandleValueChanged(e) }
                forwardValuesCallback={(e) => HandleValueChanged(e) }
                reset={props.reset}
                disabled={props.disabled}
            />

            {props.showCreateAddressOptions && (
                <AddAddressTypeButtons
                    {...props}
                    addressAddedCallback={(e) => props.newAddressCallback ? props.newAddressCallback(e) : null}
                    vcfImportedCallback={(e) => props.bulkAddressCallback ? props.bulkAddressCallback(e) : null}
                    csvImportedCallback={(e) => props.bulkAddressCallback ? props.bulkAddressCallback(e) : null}
                />
            )}
        </StackVertical>
    )
}

export { AddressControl }
